import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { getWaContext } from "../lib/waContext";

const WA_NUMBER = "254119697109";

const PAGE_HINTS = [
  ["/onboarding", "in my business onboarding"],
  ["/project/intake", "in my project intake"],
  ["/project/report", "looking at my Project Readiness Report"],
  ["/founder/readiness-report", "looking at my Capital Readiness Report"],
  ["/founder/dashboard", "on my founder dashboard"],
  ["/get-started-project", "taking the project Get Started quiz"],
  ["/get-started", "taking the Get Started quiz"],
  ["/for-business", "reading about Clinq for Business"],
  ["/for-projects", "reading about Clinq for Projects"],
  ["/for-funders", "reading about Clinq for Funders"],
  ["/funder", "in the Funder Terminal"],
];

// Pre-filled message: founder name + where they are + current wizard step.
export const buildWaMessage = ({ user, pathname, ctx }) => {
  const first = (user?.name || "").trim().split(" ")[0];
  const who = user?.role === "funder" ? "a funder" : user?.role === "founder" ? "a founder" : null;
  const hint = PAGE_HINTS.find(([p]) => pathname.startsWith(p))?.[1];
  const parts = [first ? `Hi Clinq, I'm ${first}` : "Hi Clinq"];
  if (who) parts[0] += ` (${who} on Clinq)`;
  if (ctx?.step && ctx?.label) parts.push(`I'm at Step ${ctx.step} of ${ctx.total || 5} — ${ctx.label}`);
  else if (hint) parts.push(`I'm ${hint}`);
  if (user?.role === "founder" && ctx?.company) parts.push(`Business: ${ctx.company}`);
  parts.push(user ? "I have a question:" : "I'd like to learn more about getting funder-ready");
  return parts.join(". ") + " ";
};

// Shown on every page (David is paused — see lib/features.js). Hidden on
// admin/RM back-office routes where founders never land.
const HIDE_PREFIXES = ["/admin", "/rm/", "/founder/apply", "/founder/pathway"];

export const WhatsAppFloat = () => {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const [ctx, setCtx] = useState(getWaContext());
  useEffect(() => {
    const on = (e) => setCtx(e.detail);
    window.addEventListener("clinq:wa-context", on);
    return () => window.removeEventListener("clinq:wa-context", on);
  }, []);
  if (HIDE_PREFIXES.some((p) => pathname.startsWith(p))) return null;
  const WA_URL = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(buildWaMessage({ user, pathname, ctx }))}`;
  return (
    <a
      href={WA_URL}
      target="_blank"
      rel="noreferrer"
      aria-label="Chat with Clinq on WhatsApp"
      data-testid="whatsapp-float-btn"
      className="group fixed bottom-5 right-5 z-50 flex items-center gap-0 rounded-full bg-[#25D366] p-3.5 shadow-lg shadow-black/25 transition-transform hover:scale-105"
    >
      <svg viewBox="0 0 32 32" className="h-7 w-7 fill-white" aria-hidden="true">
        <path d="M16.004 3.2c-7.06 0-12.8 5.74-12.8 12.8 0 2.26.59 4.46 1.71 6.4L3.2 28.8l6.58-1.67a12.74 12.74 0 0 0 6.22 1.6h.01c7.06 0 12.79-5.74 12.79-12.8 0-3.42-1.33-6.63-3.75-9.05a12.72 12.72 0 0 0-9.04-3.68zm0 23.36h-.01c-1.99 0-3.94-.53-5.64-1.54l-.4-.24-3.9.99 1.04-3.8-.26-.4a10.6 10.6 0 0 1-1.63-5.66c0-5.87 4.78-10.65 10.66-10.65 2.85 0 5.52 1.11 7.53 3.12a10.58 10.58 0 0 1 3.12 7.54c0 5.87-4.78 10.64-10.65 10.64zm5.84-7.97c-.32-.16-1.9-.94-2.19-1.04-.29-.11-.5-.16-.72.16-.21.32-.82 1.04-1.01 1.25-.19.21-.37.24-.69.08-.32-.16-1.35-.5-2.57-1.59-.95-.85-1.59-1.9-1.78-2.22-.19-.32-.02-.49.14-.65.14-.14.32-.37.48-.56.16-.19.21-.32.32-.53.11-.21.05-.4-.03-.56-.08-.16-.72-1.73-.98-2.37-.26-.62-.52-.54-.72-.55l-.61-.01c-.21 0-.56.08-.85.4-.29.32-1.12 1.09-1.12 2.66 0 1.57 1.15 3.09 1.31 3.3.16.21 2.26 3.45 5.47 4.84.76.33 1.36.53 1.83.68.77.24 1.47.21 2.02.13.62-.09 1.9-.78 2.17-1.53.27-.75.27-1.39.19-1.53-.08-.13-.29-.21-.61-.37z"/>
      </svg>
      <span className="max-w-0 overflow-hidden whitespace-nowrap text-sm font-bold text-white transition-all duration-300 group-hover:ml-2 group-hover:max-w-[160px]">
        Chat with us
      </span>
    </a>
  );
};
